import { 
  SolicitacaoAgendamento, 
  StatusSolicitacao, 
  TipoAcaoAudit,
  TipoNotificacao,
  Documento, 
  ArquivoPedido, 
  PedidoDigitado, 
  Reagendamento,
  Comentario
} from '../types';
import { pedidosService } from './pedidosService';
import { EmailService } from './emailService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const FRONTEND_SECRET = process.env.REACT_APP_FRONTEND_SECRET;
const getAuthToken = (): string | null => localStorage.getItem('@sistema-up:token');
const apiRequest = async (path: string, options: RequestInit = {}): Promise<any> => {
  const token = getAuthToken();
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(FRONTEND_SECRET ? { 'X-Frontend-Secret': FRONTEND_SECRET } : {}),
      ...(options.headers || {})
    }
  });
  const result = await response.json();
  if (!response.ok) throw new Error(result.error || 'Erro na API de solicitações');
  return result;
};

// ========== SERVIÇO DE SOLICITAÇÕES DE AGENDAMENTO ==========

export class SolicitacoesService {
  // Criar nova solicitação de agendamento
  static async criarSolicitacao(dados: Partial<SolicitacaoAgendamento>): Promise<{ data: SolicitacaoAgendamento | null; error: any }> {
    try {
      // Garantir que documentos é um array válido
      const dadosParaSalvar = {
        ...dados,
        documentos: dados.documentos || []
      };
      
      const result = await apiRequest('/solicitacoes-agendamento', {
        method: 'POST',
        body: JSON.stringify(dadosParaSalvar)
      });
      const data = result.data;
      
      // Converter documentos de volta para array se vier como string
      if (data && data.documentos && typeof data.documentos === 'string') {
        data.documentos = JSON.parse(data.documentos);
      }
      
      return { data, error: null };
    } catch (error) {
      console.error('Erro ao criar solicitação:', error);
      return { data: null, error };
    }
  }

  // Salvar dia fechado no banco
  static async salvarDiaFechado(data: string, filial: string, tipoFechamento: string, motivo: string): Promise<{ error: any }> {
    try {
      await apiRequest('/solicitacoes-agendamento/fechamentos', {
        method: 'POST',
        body: JSON.stringify({ data, filial, tipoFechamento, motivo })
      });
      return { error: null };
    } catch (error: any) {
      console.error('Erro ao salvar dia fechado:', error);
      return { error };
    }
  }

  // Remover dia fechado do banco
  static async removerDiaFechado(data: string, filial: string, tipoFechamento: string): Promise<{ error: any }> {
    try {
      await apiRequest('/solicitacoes-agendamento/fechamentos', {
        method: 'DELETE',
        body: JSON.stringify({ data, filial, tipoFechamento })
      });
      return { error: null };
    } catch (error: any) {
      console.error('Erro ao remover dia fechado:', error);
      return { error };
    }
  }

  // Buscar todos os dias fechados
  static async buscarDiasFechados(filial?: string): Promise<any[]> {
    try {
      const params = filial ? `?filial=${filial}` : '';
      const result = await apiRequest(`/solicitacoes-agendamento/fechamentos${params}`);
      return result.data || [];
    } catch (error: any) {
      console.error('Erro ao buscar dias fechados:', error);
      return [];
    }
  }

  // Verificar se dia está fechado para agendamento
  static async verificarDiaFechado(data: string, filial: string, tipoProduto: string): Promise<boolean> {
    try {
      // Verificar se é fim de semana (sábado = 6, domingo = 0)
      // Forçar interpretação local para evitar problemas de fuso horário
      const dataObj = new Date(data + 'T12:00:00');
      const diaSemana = dataObj.getDay();
      if (diaSemana === 0 || diaSemana === 6) {
        return true;
      }

      const apiResult = await apiRequest(
        `/solicitacoes-agendamento/fechamentos/verificar?data=${data}&filial=${filial}&tipoProduto=${tipoProduto}`
      );
      return apiResult.fechado || false;
    } catch (error: any) {
      console.error('Erro ao verificar dia fechado:', error);
      return false;
    }
  }

  // Buscar todas as solicitações
  static async buscarTodas(filters?: any): Promise<SolicitacaoAgendamento[]> {
    try {
      const params = new URLSearchParams();
      
      if (filters?.status) {
        params.set('status', filters.status.join(','));
      }
      if (filters?.filial) params.set('filial', filters.filial);
      if (filters?.solicitanteId) params.set('solicitanteId', filters.solicitanteId);

      const queryString = params.toString() ? `?${params.toString()}` : '';
      const apiResult = await apiRequest(`/solicitacoes-agendamento${queryString}`);
      const data = apiResult.data || [];
      
      // Converter documentos de string JSON para array
      if (data && data.length > 0) {
        data.forEach((sol: any, i: number) => {
          // Se documentos for string, converter para array
          if (sol.documentos && typeof sol.documentos === 'string') {
            try {
              sol.documentos = JSON.parse(sol.documentos);
            } catch (e) {
              console.error('Erro ao converter documentos:', e);
              sol.documentos = [];
            }
          }
          
          if (sol.documentos_digitados && typeof sol.documentos_digitados === 'string') {
            try {
              sol.documentos_digitados = JSON.parse(sol.documentos_digitados);
            } catch (e) {
              console.error('Erro ao converter documentos_digitados:', e);
              sol.documentos_digitados = [];
            }
          }
          
          if (sol.documentos && sol.documentos.length > 0) {
            // Documentos processados
          }
        });
      }
      
      return data || [];
    } catch (error) {
      console.error('Erro ao buscar solicitações:', error);
      throw error;
    }
  }

  // Solicitar pedido de compra a partir de uma solicitação
  static async solicitarPedidoCompra(solicitacaoId: string, usuarioId: string): Promise<{ data: SolicitacaoAgendamento | null; error: any }> {
    try {
      const fetchResult = await apiRequest(`/solicitacoes-agendamento/${solicitacaoId}`);
      const solicitacaoDados = fetchResult.data;
      if (!solicitacaoDados) {
        throw new Error('Solicitação não encontrada');
      }

      // 1. Criar pedido de compra com dados da solicitação
      const pedidoCriado = await pedidosService.criarPedido({
        solicitacao_id: solicitacaoId,
        fornecedor: solicitacaoDados?.fornecedor || 'Fornecedor',
        data_desejada: solicitacaoDados?.data_desejada || new Date().toISOString().split('T')[0],
        peso: solicitacaoDados?.peso || 0,
        volume: solicitacaoDados?.volume ? parseFloat(solicitacaoDados.volume) : 0,
        solicitante_id: usuarioId,
        observacoes: `Pedido criado a partir da solicitação ${solicitacaoId}`,
        filial: solicitacaoDados?.filial || 'FILIAL_1'
      });

      if (pedidoCriado.error) {
        console.error('Erro ao criar pedido:', pedidoCriado.error);
        throw pedidoCriado.error;
      }

      const updateResult = await apiRequest(`/solicitacoes-agendamento/${solicitacaoId}/pedido-compra`, {
        method: 'PATCH',
        body: JSON.stringify({
          status: StatusSolicitacao.AGUARDANDO_PEDIDO,
          pedido_compra_id: pedidoCriado.data?.id
        })
      });

      return { data: updateResult.data, error: null };
    } catch (error) {
      console.error('Erro ao solicitar pedido de compra:', error);
      return { data: null, error };
    }
  }

  // Confirmar digitação do pedido e atualizar status da solicitação
  static async confirmarPedidoDigitado(pedidoId: string, solicitacaoId: string): Promise<{ data: SolicitacaoAgendamento | null; error: any }> {
    try {
      const result = await apiRequest(`/solicitacoes-agendamento/${solicitacaoId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'Pedido Digitado' })
      });

      return { data: result.data, error: null };
    } catch (error) {
      console.error('Erro ao confirmar pedido digitado:', error);
      return { data: null, error };
    }
  }

  // Aprovar solicitação final e converter em carga
  static async aprovarSolicitacao(solicitacaoId: string): Promise<{ data: SolicitacaoAgendamento | null; error: any }> {
    try {
      // 1. Buscar dados completos da solicitação
      const solicitacao = await this.buscarPorId(solicitacaoId);
      if (!solicitacao) {
        throw new Error('Solicitação não encontrada');
      }

      const statusResult = await apiRequest(`/solicitacoes-agendamento/${solicitacaoId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'Aprovado' })
      });
      const data = statusResult.data;

      // 3. Enviar email de aprovação (implementação futura quando tivermos email do solicitante)
      // TODO: Buscar email do usuário pelo solicitante_id e enviar notificação
      // if (solicitacao.solicitante_email) {
      //   try {
      //     await EmailService.enviarEmailAprovacaoSolicitacao(
      //       solicitacao.solicitante_email,
      //       solicitacao.fornecedor || 'Fornecedor',
      //       solicitacao.id
      //     );
      //   } catch (emailError) {
      //     console.error('Erro ao enviar email de aprovação:', emailError);
      //   }
      // }

      // 4. Converter solicitação aprovada em carga
      const CargasLogisticaService = require('./cargasLogisticaService').default;
      const resultadoCarga = await CargasLogisticaService.converterSolicitacaoParaCarga(solicitacao);

      if (resultadoCarga.error) {
        console.error('Erro ao criar carga:', resultadoCarga.error);
        // Não falhar a aprovação se a carga não for criada, mas logar o erro
      } else {
        // Carga criada com sucesso
      }

      return { data, error: null };
    } catch (error: any) {
      console.error('Erro ao aprovar solicitação:', error);
      return { data: null, error };
    }
  }

  // Reprovar solicitação
  static async reprovarSolicitacao(solicitacaoId: string, motivo: string): Promise<{ data: SolicitacaoAgendamento | null; error: any }> {
    try {
      const result = await apiRequest(`/solicitacoes-agendamento/${solicitacaoId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status: 'Reprovado', motivo_reprovacao: motivo })
      });

      return { data: result.data, error: null };
    } catch (error: any) {
      console.error('Erro ao reprovar solicitação:', error);
      return { data: null, error };
    }
  }

  // Buscar solicitação por ID
  static async buscarPorId(id: string): Promise<SolicitacaoAgendamento | null> {
    try {
      const result = await apiRequest(`/solicitacoes-agendamento/${id}`);
      return result.data;
    } catch (error) {
      console.error('Erro ao buscar solicitação:', error);
      return null;
    }
  }

  // Criar nova solicitação
  static async criar(solicitacao: Omit<SolicitacaoAgendamento, 'id' | 'data_solicitacao' | 'data_atualizacao'>): Promise<SolicitacaoAgendamento> {
    try {
      const novaSolicitacao = {
        ...solicitacao,
        id: crypto.randomUUID(),
        data_solicitacao: new Date().toISOString(),
        data_atualizacao: new Date().toISOString(),
        prioridade: this.calcularPrioridade(solicitacao.data_desejada)
      };

      const result = await apiRequest('/solicitacoes-agendamento', {
        method: 'POST',
        body: JSON.stringify(novaSolicitacao)
      });
      const data = result.data;

      // 🔔 Notificar equipe sobre nova solicitação
      try {
        const { NotificacoesService } = await import('./notificacoesService');
        await NotificacoesService.notificarEquipeNovaSolicitacao(
          data.id,
          data.fornecedor || 'Fornecedor não informado',
          data.tipo_produto || 'Não informado',
          data.data_desejada || 'Não informada',
          data.filial || 'FILIAL_1',
          solicitacao.solicitante_id // Excluir solicitante das notificações
        );
      } catch (notifError) {
        console.error('❌ Erro ao enviar notificação da equipe:', notifError);
        // Não falhar a criação se a notificação falhar
      }

      return data;
    } catch (error) {
      console.error('Erro ao criar solicitação:', error);
      throw error;
    }
  }

  // Atualizar status da solicitação
  static async atualizarStatus(
    id: string,
    status: StatusSolicitacao,
    motivo?: string
  ): Promise<SolicitacaoAgendamento> {
    try {
      const result = await apiRequest(`/solicitacoes-agendamento/${id}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status, ...(motivo && { motivo_reprovacao: motivo }) })
      });
      return result.data;
    } catch (error) {
      console.error('❌ Erro ao atualizar status:', error);
      throw error;
    }
  }

  // Atualizar ordem de chegada e horário da solicitação
  static async atualizarOrdemChegada(
    id: string,
    ordem: number,
    hora: string
  ): Promise<SolicitacaoAgendamento> {
    try {
      const result = await apiRequest(`/solicitacoes-agendamento/${id}/ordem-chegada`, {
        method: 'PATCH',
        body: JSON.stringify({ ordem_chegada: ordem, hora_ordem_chegada: hora })
      });
      return result.data;
    } catch (error) {
      console.error('❌ Erro ao atualizar ordem de chegada:', error);
      throw error;
    }
  }

  // Aprovar solicitação
  static async aprovar(id: string): Promise<SolicitacaoAgendamento> {
    try {
      // Primeiro atualizar o status da solicitação
      const solicitacao = await this.atualizarStatus(id, StatusSolicitacao.AGUARDANDO_PEDIDO);
      
      // Criar pedido automaticamente vinculado à solicitação
      await pedidosService.criarPedidoDeSolicitacao(id, solicitacao.solicitante_id);
      
      return solicitacao;
    } catch (error) {
      console.error('Erro ao aprovar solicitação e criar pedido:', error);
      throw error;
    }
  }

  // Reprovar solicitação
  static async reprovar(id: string, motivo: string): Promise<SolicitacaoAgendamento> {
    const resultado = await this.atualizarStatus(id, StatusSolicitacao.REPROVADO, motivo);
    return resultado;
  }

  // Propor reagendamento
  static async proporReagendamento(
    id: string, 
    novaData: string, 
    motivo: string,
    usuarioId: string
  ): Promise<SolicitacaoAgendamento> {
    try {
      const reagendamento: Reagendamento = {
        novaData,
        motivo,
        propostoPor: usuarioId,
        dataProposta: new Date().toISOString()
      };

      const result = await apiRequest(`/solicitacoes-agendamento/${id}/reagendar`, {
        method: 'PATCH',
        body: JSON.stringify({
          novaData,
          motivo,
          status: StatusSolicitacao.REAGENDADO
        })
      });
      return result.data;
    } catch (error) {
      console.error('Erro ao propor reagendamento:', error);
      throw error;
    }
  }

  // Aceitar reagendamento (solicitante)
  static async aceitarReagendamento(solicitacaoId: string): Promise<{ data: SolicitacaoAgendamento | null; error: any }> {
    try {
      const result = await apiRequest(`/solicitacoes-agendamento/${solicitacaoId}/aceitar-reagendamento`, {
        method: 'POST'
      });
      return { data: result.data, error: null };
    } catch (error: any) {
      console.error('Erro ao aceitar reagendamento:', error);
      return { data: null, error };
    }
  }

  // Rejeitar reagendamento (solicitante)
  static async rejeitarReagendamento(solicitacaoId: string, motivo: string): Promise<{ data: SolicitacaoAgendamento | null; error: any }> {
    try {
      const result = await apiRequest(`/solicitacoes-agendamento/${solicitacaoId}/rejeitar-reagendamento`, {
        method: 'POST',
        body: JSON.stringify({ motivo })
      });
      return { data: result.data, error: null };
    } catch (error: any) {
      console.error('Erro ao rejeitar reagendamento:', error);
      return { data: null, error };
    }
  }

  // Adicionar documento
  static async adicionarDocumento(
    solicitacaoId: string,
    documento: Omit<Documento, 'id' | 'uploadedAt'>
  ): Promise<Documento> {
    try {
      const novoDocumento: Documento = {
        ...documento,
        id: crypto.randomUUID(),
        uploadedAt: new Date().toISOString()
      };

      // Buscar solicitação atual
      const solicitacao = await this.buscarPorId(solicitacaoId);
      if (!solicitacao) throw new Error('Solicitação não encontrada');

      // Adicionar documento ao array
      const documentosAtualizados = [...(solicitacao.documentos || []), novoDocumento];

      await apiRequest(`/solicitacoes-agendamento/${solicitacaoId}/documentos`, {
        method: 'PATCH',
        body: JSON.stringify({ documentos: documentosAtualizados })
      });
      return novoDocumento;
    } catch (error) {
      console.error('Erro ao adicionar documento:', error);
      throw error;
    }
  }

  // Adicionar arquivo de pedido
  static async adicionarArquivoPedido(
    solicitacaoId: string,
    arquivoPedido: Omit<ArquivoPedido, 'id' | 'uploadedAt'>
  ): Promise<ArquivoPedido> {
    try {
      const novoArquivo: ArquivoPedido = {
        ...arquivoPedido,
        id: crypto.randomUUID(),
        uploadedAt: new Date().toISOString()
      };

      await apiRequest(`/solicitacoes-agendamento/${solicitacaoId}/arquivo-pedido`, {
        method: 'PATCH',
        body: JSON.stringify({
          arquivo_pedido: novoArquivo,
          status: StatusSolicitacao.PEDIDO_DIGITADO
        })
      });
      return novoArquivo;
    } catch (error) {
      console.error('Erro ao adicionar arquivo de pedido:', error);
      throw error;
    }
  }

  // Adicionar pedido digitado
  static async adicionarPedidoDigitado(
    solicitacaoId: string,
    pedidoDigitado: Omit<PedidoDigitado, 'id'>
  ): Promise<PedidoDigitado> {
    try {
      const novoPedido: PedidoDigitado = {
        ...pedidoDigitado,
        id: crypto.randomUUID()
      };

      // Buscar solicitação atual
      const solicitacao = await this.buscarPorId(solicitacaoId);
      if (!solicitacao) throw new Error('Solicitação não encontrada');

      // Adicionar pedido ao array
      const pedidosAtualizados = [...(solicitacao.documentos_digitados || []), novoPedido];

      await apiRequest(`/solicitacoes-agendamento/${solicitacaoId}/pedidos-digitados`, {
        method: 'PATCH',
        body: JSON.stringify({ documentos_digitados: pedidosAtualizados })
      });
      return novoPedido;
    } catch (error) {
      console.error('Erro ao adicionar pedido digitado:', error);
      throw error;
    }
  }

  // Adicionar comentário
  static async adicionarComentario(
    solicitacaoId: string,
    texto: string,
    usuarioId: string,
    usuarioNome: string
  ): Promise<Comentario> {
    try {
      const novoComentario: Comentario = {
        id: crypto.randomUUID(),
        texto,
        usuarioId,
        usuarioNome,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      // Buscar solicitação atual
      const solicitacao = await this.buscarPorId(solicitacaoId);
      if (!solicitacao) throw new Error('Solicitação não encontrada');

      // Adicionar comentário ao array
      const comentariosAtualizados = [...(solicitacao.comentarios || []), novoComentario];

      await apiRequest(`/solicitacoes-agendamento/${solicitacaoId}/comentarios`, {
        method: 'PATCH',
        body: JSON.stringify({ comentarios: comentariosAtualizados })
      });
      return novoComentario;
    } catch (error) {
      console.error('Erro ao adicionar comentário:', error);
      throw error;
    }
  }

  // Excluir solicitação
  static async excluir(id: string): Promise<void> {
    try {
      await apiRequest(`/solicitacoes-agendamento/${id}`, { method: 'DELETE' });
    } catch (error) {
      console.error('Erro ao excluir solicitação:', error);
      throw error;
    }
  }

  // Calcular prioridade baseado na data desejada
  private static calcularPrioridade(dataDesejada: string): number {
    const hoje = new Date();
    const data = new Date(dataDesejada);
    const diasDiferenca = Math.floor((data.getTime() - hoje.getTime()) / (1000 * 60 * 60 * 24));

    // Prioridade: quanto mais próximo, maior a prioridade
    if (diasDiferenca <= 1) return 5; // Crítico
    if (diasDiferenca <= 3) return 4; // Alta
    if (diasDiferenca <= 7) return 3; // Média
    if (diasDiferenca <= 14) return 2; // Baixa
    return 1; // Normal
  }

  // Dados mock para testes
  private static mockSolicitacoes: SolicitacaoAgendamento[] = [
    {
      id: 'mock-sol-1',
      solicitante_id: 'user-1',
      solicitante_nome: 'João Silva',
      fornecedor: 'Fornecedor Teste A',
      data_desejada: '2024-04-19',
      peso: 1500,
      volume: '10.5',
      tipo_produto: 'geral',
      tipo_carga: 'paletizada',
      conferente: 'Carlos Souza',
      status: StatusSolicitacao.PENDENTE,
      documentos: [
        {
          id: 'doc-1',
          nome: 'documento_fornecedor.pdf',
          tipo: 'application/pdf',
          tamanho: 2.3 * 1024 * 1024,
          fornecedorMercadoria: 'Produto A',
          storagePath: 'docs/doc-1.pdf',
          uploadedAt: '2024-04-18T10:00:00Z',
          uploadedBy: 'user-1'
        }
      ],
      arquivo_pedido: undefined,
      documentos_digitados: [],
      reagendamento_proposto: undefined,
      comentarios: [],
      mercadoria_a_enviar: 'Solicitação de teste mock',
      data_solicitacao: '2024-04-18T10:00:00Z',
      data_atualizacao: '2024-04-18T10:00:00Z',
      filial: 'FILIAL_1',
      prioridade: 4,
      motivo_reprovacao: undefined
    },
    {
      id: 'mock-sol-2',
      solicitante_id: 'user-2',
      solicitante_nome: 'Maria Santos',
      fornecedor: 'Fornecedor Teste B',
      data_desejada: '2024-04-20',
      peso: 2300,
      volume: '15.2',
      tipo_produto: 'frios',
      tipo_carga: 'solta',
      conferente: 'Ana Costa',
      status: StatusSolicitacao.PENDENTE,
      documentos: [
        {
          id: 'doc-2',
          nome: 'cotacao_fornecedor.xlsx',
          tipo: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          tamanho: 1.2 * 1024 * 1024,
          fornecedorMercadoria: 'Produto B',
          storagePath: 'docs/doc-2.xlsx',
          uploadedAt: '2024-04-18T11:00:00Z',
          uploadedBy: 'user-2'
        }
      ],
      arquivo_pedido: undefined,
      documentos_digitados: [],
      reagendamento_proposto: undefined,
      comentarios: [],
      mercadoria_a_enviar: 'Outra solicitação de teste',
      data_solicitacao: '2024-04-18T11:00:00Z',
      data_atualizacao: '2024-04-18T11:00:00Z',
      filial: 'FILIAL_1',
      prioridade: 3,
      motivo_reprovacao: undefined
    },
    {
      id: 'mock-sol-3',
      solicitante_id: 'user-3',
      solicitante_nome: 'Pedro Oliveira',
      fornecedor: 'Fornecedor Teste C',
      data_desejada: '2024-04-18',
      peso: 1800,
      volume: '12.0',
      tipo_produto: 'seco',
      tipo_carga: 'paletizada',
      conferente: 'Luiz Fernandes',
      status: StatusSolicitacao.PENDENTE,
      documentos: [
        {
          id: 'doc-3',
          nome: 'catalogo_produtos.pdf',
          tipo: 'application/pdf',
          tamanho: 3.1 * 1024 * 1024,
          fornecedorMercadoria: 'Produto C',
          storagePath: 'docs/doc-3.pdf',
          uploadedAt: '2024-04-18T09:00:00Z',
          uploadedBy: 'user-3'
        }
      ],
      arquivo_pedido: undefined,
      documentos_digitados: [],
      reagendamento_proposto: undefined,
      comentarios: [],
      mercadoria_a_enviar: 'Solicitação urgente mock',
      data_solicitacao: '2024-04-18T09:00:00Z',
      data_atualizacao: '2024-04-18T09:00:00Z',
      filial: 'FILIAL_1',
      prioridade: 5, // Hoje - prioridade máxima
      motivo_reprovacao: undefined
    },
    {
      id: 'mock-sol-4',
      solicitante_id: 'user-4',
      solicitante_nome: 'Carla Mendes',
      fornecedor: 'Fornecedor Teste D',
      data_desejada: '2024-04-25',
      peso: 3000,
      volume: '20.0',
      tipo_produto: 'quimico',
      tipo_carga: 'solta',
      conferente: 'Roberto Almeida',
      status: StatusSolicitacao.AGUARDANDO_PEDIDO,
      pedido_compra_id: 'mock-pedido-1',
      documentos: [
        {
          id: 'doc-4',
          nome: 'ficha_tecnica.pdf',
          tipo: 'application/pdf',
          tamanho: 1.8 * 1024 * 1024,
          fornecedorMercadoria: 'Produto D',
          storagePath: 'docs/doc-4.pdf',
          uploadedAt: '2024-04-18T08:00:00Z',
          uploadedBy: 'user-4'
        }
      ],
      arquivo_pedido: undefined,
      documentos_digitados: [],
      reagendamento_proposto: undefined,
      comentarios: [],
      mercadoria_a_enviar: 'Solicitação de teste mock - Já solicitou pedido',
      data_solicitacao: '2024-04-18T08:00:00Z',
      data_atualizacao: '2024-04-18T08:00:00Z',
      filial: 'FILIAL_1',
      prioridade: 2, // 7+ dias - prioridade baixa
      motivo_reprovacao: undefined
    },
    {
      id: 'mock-sol-5',
      solicitante_id: 'user-5',
      solicitante_nome: 'Lucas Costa',
      fornecedor: 'Fornecedor Teste E',
      data_desejada: '2024-04-22',
      peso: 1800,
      volume: '12.3',
      tipo_produto: 'alimenticio',
      tipo_carga: 'paletizada',
      conferente: 'Mariana Silva',
      status: StatusSolicitacao.PEDIDO_DIGITADO,
      pedido_compra_id: 'mock-pedido-2',
      documentos: [
        {
          id: 'doc-5',
          nome: 'nota_fiscal.pdf',
          tipo: 'application/pdf',
          tamanho: 3.5 * 1024 * 1024,
          fornecedorMercadoria: 'Produto E',
          storagePath: 'docs/doc-5.pdf',
          uploadedAt: '2024-04-18T07:00:00Z',
          uploadedBy: 'user-5'
        }
      ],
      arquivo_pedido: undefined,
      documentos_digitados: [],
      reagendamento_proposto: undefined,
      comentarios: [],
      mercadoria_a_enviar: 'Solicitação de teste mock - Pedido já digitado',
      data_solicitacao: '2024-04-18T07:00:00Z',
      data_atualizacao: '2024-04-18T07:00:00Z',
      filial: 'FILIAL_1',
      prioridade: 3, // 4 dias - prioridade média
      motivo_reprovacao: undefined
    }
  ];

  // Buscar solicitações pendentes para aprovação
  static async buscarPendentes(filial?: string): Promise<SolicitacaoAgendamento[]> {
    const statusPendentes: StatusSolicitacao[] = [
      StatusSolicitacao.PENDENTE
    ];
    return this.buscarTodas({ status: statusPendentes, filial });
  }

  // Buscar solicitações aguardando pedido
  static async buscarAguardandoPedido(filial?: string): Promise<SolicitacaoAgendamento[]> {
    const statusAguardando: StatusSolicitacao[] = [
      StatusSolicitacao.AGUARDANDO_PEDIDO
    ];
    const solicitacoes = await this.buscarTodas({ status: statusAguardando, filial });

    // Buscar pedidos vinculados para verificar se já foram digitados
    const { data: pedidos } = await pedidosService.buscarPedidos({ filial });

    // Filtrar para mostrar apenas solicitações cujo pedido NÃO foi digitado
    return solicitacoes.filter(solicitacao => {
      if (!solicitacao.pedido_compra_id) return true; // Sem pedido, deve aparecer

      const pedidoVinculado = pedidos?.find((p: any) => p.id === solicitacao.pedido_compra_id);
      // Se o pedido não tem numero_pedido e não tem arquivo_id, ainda não foi digitado
      return !pedidoVinculado?.numero_pedido && !pedidoVinculado?.arquivo_id;
    });
  }

  // Buscar solicitações com pedido digitado
  static async buscarPedidoDigitado(filial?: string): Promise<SolicitacaoAgendamento[]> {
    const statusDigitado: StatusSolicitacao[] = [
      StatusSolicitacao.PEDIDO_DIGITADO
    ];
    return this.buscarTodas({ status: statusDigitado, filial });
  }

  // Buscar solicitações por status específico
  static async buscarPorStatus(status: StatusSolicitacao, filial?: string): Promise<SolicitacaoAgendamento[]> {
    return this.buscarTodas({ status: [status], filial });
  }

  // Buscar todas as solicitações (para a aba "todas")
  static async buscarTodasSolicitacoes(filial?: string): Promise<SolicitacaoAgendamento[]> {
    return this.buscarTodas({ filial });
  }

  // Buscar solicitações do usuário
  static async buscarDoUsuario(usuarioId: string, filial?: string): Promise<SolicitacaoAgendamento[]> {
    return this.buscarTodas({ solicitanteId: usuarioId, filial });
  }

  // Buscar solicitações por fornecedor
  static async buscarPorFornecedor(fornecedor: string, filial?: string, apenasFuturos: boolean = false): Promise<SolicitacaoAgendamento[]> {
    try {
      const params = new URLSearchParams();
      params.append('fornecedor', fornecedor);
      if (filial) params.append('filial', filial);
      params.append('apenasFuturos', apenasFuturos.toString());

      const result = await apiRequest(`/solicitacoes-agendamento/por-fornecedor?${params}`);
      return result.data || [];
    } catch (error) {
      console.error('Erro ao buscar solicitações por fornecedor:', error);
      return [];
    }
  }
}

export default SolicitacoesService;
